/* ---------------------------------------------------------------------------
 *  src/index.js
 * ---------------------------------------------------------------------------
 *  – Hydrate both user & creator token stores
 *  – If on ButtPay domain and at root, redirect to ButtPay landing
 *  – Otherwise, if *either* token exists (and we're not already on a deep-link),
 *    move the browser history to the right start route *before* React mounts.
 *    This guarantees the correct page flashes first, even on hard refreshes.
 * ------------------------------------------------------------------------ */
import React                 from 'react';
import ReactDOM              from 'react-dom/client';
import App                   from './App';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Capacitor }         from '@capacitor/core';
import {
  initAuthStorage,
  getAuthToken           as getUserToken,
} from './API/token-util';
import {
  initCreatorAuthStorage,
  getAuthToken           as getCreatorToken,
} from './API/creatorTokenUtil';
import routes              from './Config/routes';
import './index.css';
import { initializeSentry } from './Utils/sentryConfig';

// ────────────────────────────────────────────────────────────
// Google OAuth client-ID (runtime platform switch)
// ────────────────────────────────────────────────────────────
const IOS_ID = '489265257959-374fsc04dffos29tto4jsqcd2sch5f9o.apps.googleusercontent.com';
const WEB_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID ?? '';
const googleClientId =
  Capacitor.getPlatform() === 'ios' ? IOS_ID : WEB_ID;

// ────────────────────────────────────────────────────────────
// Utility to check if we're on ButtPay domain
// ────────────────────────────────────────────────────────────
const isButtPayDomain = () => {
  const hostname = window.location.hostname;
  return hostname === 'www.buttpay.com' || 
         hostname === 'buttpay.com' ||
         (process.env.NODE_ENV === 'development' && 
          window.location.search.includes('buttpay=true'));
};

// ────────────────────────────────────────────────────────────
// Initialize Sentry error tracking (before React mounts)
// ────────────────────────────────────────────────────────────
initializeSentry();

// ────────────────────────────────────────────────────────────
// BOOTSTRAP
// ────────────────────────────────────────────────────────────
(async () => {
  // 1) Hydrate both token stores (Keychain / cookies → memory)
  await Promise.all([
    initAuthStorage(),          // user role
    initCreatorAuthStorage(),   // creator role
  ]);

  // 2) Check if we're on ButtPay domain - redirect to ButtPay landing if at root
  const isButtPay = isButtPayDomain();
  const isRoot = window.location.pathname === '/' || window.location.pathname === routes.base;

  if (isButtPay && isRoot) {
    // ButtPay domain always goes to ButtPay landing page at root
    window.history.replaceState({}, '', routes.buttPay);
  } else if (!isButtPay) {
    // 3) For Blitz domain, decide where we *should* start based on auth
    const creatorJWT = getCreatorToken();
    const userJWT    = getUserToken();
    let startPath = null;
    
    if (creatorJWT)       startPath = routes.creatorConnectStart; // e.g. "/creator"
    else if (userJWT)     startPath = routes.dashboard;           // e.g. "/dashboard"
    
    // Avoid clobbering deep-links or explicit URLs
    const shouldBump  = startPath && isRoot;
    
    if (shouldBump) {
      // Replace the current history entry so back-button still works nicely
      window.history.replaceState({}, '', startPath);
    }
  }

  // 4) Mount React
  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <React.StrictMode>
      <GoogleOAuthProvider clientId={googleClientId}>
        <App />
      </GoogleOAuthProvider>
    </React.StrictMode>
  );
})();